import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase-client-wrapper';
import { clearRecoveryMode } from '@/lib/auth-recovery-interceptor';
import { getRedirectUrl } from '@/lib/constants';
import { getPostAuthPath } from '@/lib/post-auth-redirect';
import { getPendingQuickBooksReturnTo } from '@/lib/auth-quickbooks-return';
import { getSupabaseFunctionErrorMessage } from '@/lib/supabase-function-error';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Gift, ArrowLeft } from 'lucide-react';
import GoogleLogo from '@/components/ui/google-logo';
import { ReferralCodesModal } from '@/components/ReferralCodesModal';
import { AuthPageLayout } from '@/components/auth/AuthPageLayout';
import { morphSpringSoft } from '@/lib/motion';

const UnderlineInput = ({
  type = "text",
  placeholder,
  value,
  onChange,
  required = false,
  icon
}: {
  type?: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  icon?: React.ReactNode;
}) => (
  <div className={`auth-field relative${icon ? ' with-icon' : ''}`}>
    {icon && <div className="auth-field-icon">{icon}</div>}
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
    />
  </div>
);

const Auth = () => {
  const { signIn, signUp, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Check for recovery mode SYNCHRONOUSLY during initial render (before Supabase clears the hash)
  const [showSetNewPassword, setShowSetNewPassword] = useState(() => {
    const hash = window.location.hash;
    const isRecoveryFromHash = hash && hash.includes('type=recovery');
    const isRecoveryFromStorage = sessionStorage.getItem('passwordRecoveryMode') === 'true';
    
    if (isRecoveryFromHash) {
      console.log('[Auth] SYNC: Detected recovery from hash, setting sessionStorage');
      sessionStorage.setItem('passwordRecoveryMode', 'true');
      return true;
    }
    if (isRecoveryFromStorage) {
      console.log('[Auth] SYNC: Detected recovery from sessionStorage');
      return true;
    }
    return false;
  });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [showResendConfirm, setShowResendConfirm] = useState(false);
  
  // Team invitation params
  const [teamInvite, setTeamInvite] = useState<{ teamId: string; role: string; email: string } | null>(null);

  // Check for team invitation params and password reset on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const teamId = params.get('team');
    const role = params.get('role');
    const inviteEmail = params.get('email');
    
    if (teamId && role && inviteEmail) {
      setTeamInvite({ teamId, role, email: inviteEmail });
      setEmail(inviteEmail);
      setShowSignUp(true); // Show signup form for invited users
    }
    
    // Check URL hash for recovery mode
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      console.log('[Auth] Detected password recovery in useEffect');
      sessionStorage.setItem('passwordRecoveryMode', 'true');
      setShowSetNewPassword(true);
      setShowForgotPassword(false);
      setShowSignUp(false);
    }
    
    // Also check if we already have the flag set (page might have refreshed)
    if (sessionStorage.getItem('passwordRecoveryMode') === 'true') {
      console.log('[Auth] Recovery mode flag found in sessionStorage');
      setShowSetNewPassword(true);
      setShowForgotPassword(false);
      setShowSignUp(false);
    }
  }, []);

  // Manual token exchange for recovery mode (when detectSessionInUrl is disabled)
  useEffect(() => {
    const handleRecoveryTokenExchange = async () => {
      const isRecovery = sessionStorage.getItem('passwordRecoveryMode') === 'true';
      if (!isRecovery) return;
      
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const type = params.get('type');
      
      if (type === 'recovery' && accessToken && refreshToken) {
        console.log('[Auth] Recovery mode - manually setting session');
        
        // Set the session manually without triggering redirect
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        
        if (error) {
          console.error('[Auth] Failed to set recovery session:', error);
          toast({
            title: "Error",
            description: "Failed to verify recovery link. Please request a new one.",
            variant: "destructive"
          });
          clearRecoveryMode();
          return;
        }
        
        // Clear the hash from URL to prevent re-processing
        window.history.replaceState(null, '', window.location.pathname);
        
        // Ensure password form is shown
        setShowSetNewPassword(true);
        setShowForgotPassword(false);
        setShowSignUp(false);
        
        console.log('[Auth] Recovery session set successfully, showing password form');
      }
    };
    
    handleRecoveryTokenExchange();
  }, [toast]);

  // Check for password recovery / email confirmation events
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (import.meta.env.DEV) {
        console.log('[Auth] Auth state changed:', event);
      }
      if (event === 'PASSWORD_RECOVERY') {
        setShowSetNewPassword(true);
        setShowForgotPassword(false);
        setShowSignUp(false);
      }
      if (event === 'SIGNED_IN') {
        const hash = window.location.hash;
        if (hash.includes('type=signup') || hash.includes('type=email')) {
          toast({
            title: 'Email confirmed',
            description: 'Your account is verified. Welcome to Vesta CFO!',
          });
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [toast]);

  useEffect(() => {
    document.title = 'Sign in · Vesta CFO';
  }, []);

  // After Google OAuth: return to /auth (same origin) so PKCE ?code= is not stripped.
  // /onboarding is behind ProtectedRoute; unauthenticated first paint redirected to /auth without the query → broken session → lands like logged out (often /).
  useEffect(() => {
    const hash = window.location.hash;
    const isRecoveryFromHash = hash && hash.includes('type=recovery');
    const isRecoveryFromStorage = sessionStorage.getItem('passwordRecoveryMode') === 'true';

    if (isRecoveryFromHash || isRecoveryFromStorage) {
      console.log('[Auth] Recovery mode active, blocking redirect');
      setShowSetNewPassword(true);
      return;
    }

    if (!user || loading || showSetNewPassword) return;

    let cancelled = false;

    void (async () => {
      if (localStorage.getItem('vesta_pending_google_signup') === '1') {
        localStorage.removeItem('vesta_pending_google_signup');
        if (!cancelled) navigate('/onboarding', { replace: true });
        return;
      }

      const returnTo = getPendingQuickBooksReturnTo(location);
      if (returnTo) {
        try {
          sessionStorage.removeItem('vesta_oauth_return_path');
        } catch {
          /* ignore */
        }
        if (!cancelled) {
          navigate(
            {
              pathname: returnTo.pathname,
              search: returnTo.search,
              hash: returnTo.hash ?? '',
            },
            { replace: true },
          );
        }
        return;
      }

      const path = await getPostAuthPath(user.id);
      if (!cancelled) navigate(path, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [user, loading, navigate, location, showSetNewPassword]);

  const handleResendConfirmation = async () => {
    if (!email) {
      toast({
        title: "Email required",
        description: "Enter your email address first.",
        variant: "destructive",
      });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: getRedirectUrl('/auth', import.meta.env.DEV),
        },
      });
      if (error) throw error;
      toast({
        title: "Confirmation email sent",
        description: "Check your inbox (and spam) for the Vesta CFO confirmation link.",
      });
    } catch (error: any) {
      toast({
        title: "Could not resend email",
        description: error.message || "Please try again in a few minutes.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Missing information",
        description: "Please enter both email and password.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    
    const { error } = await signIn(email, password);
    if (error) {
      const needsConfirmation = /confirm/i.test(error.message);
      setShowResendConfirm(needsConfirmation);
      toast({
        title: needsConfirmation ? "Confirm your email" : "Sign in failed",
        description: needsConfirmation
          ? "Check your inbox for the Vesta CFO confirmation link, then sign in again."
          : error.message === "Invalid login credentials"
            ? "Invalid email or password."
            : error.message,
        variant: "destructive"
      });
      setIsLoading(false);
    } else {
      // Sign in succeeded - get user's name for the animation
      const { data } = await supabase.auth.getUser();
      const userFirstName = data.user?.user_metadata?.full_name?.split(' ')[0] 
        || data.user?.email?.split('@')[0] 
        || 'there';
      
      // ALWAYS check for pending team invitations by email (even without URL params)
      // This handles users who sign in without clicking the invitation link
      if (data.user) {
        try {
          const userEmail = data.user.email || email;
          
          // Find ANY pending invitation for this email
          const { data: invitation } = await supabase
            .from('team_invitations')
            .select('id, team_id, role')
            .eq('email', userEmail.toLowerCase())
            .is('accepted_at', null)
            .maybeSingle();
          
          if (invitation) {
            console.log('[Auth] Found pending invitation for email, accepting...', invitation);
            
            // Use secure function to accept invitation
            const { data: result, error: acceptError } = await supabase.rpc('accept_team_invitation', {
              p_invitation_id: invitation.id,
              p_user_id: data.user.id
            });
            
            const resultObj = result as { success?: boolean; team_id?: string } | null;
            if (!acceptError && resultObj?.success) {
              // Notify team owner
              await supabase.functions.invoke('team-notifications', {
                body: {
                  type: 'member_joined',
                  teamId: invitation.team_id,
                  memberEmail: userEmail,
                  memberName: userFirstName,
                },
              });
              
              toast({ title: "Welcome to the team!", description: "You've been added successfully." });
            } else if (acceptError) {
              console.error('[Auth] Team join error:', acceptError);
            }
          }
        } catch (err) {
          console.error('[Auth] Team invitation check error:', err);
        }
      }
      
      const qbResume = getPendingQuickBooksReturnTo(location);
      if (qbResume) {
        try {
          sessionStorage.removeItem('vesta_oauth_return_path');
        } catch {
          /* ignore */
        }
        navigate(
          {
            pathname: qbResume.pathname,
            search: qbResume.search,
            hash: qbResume.hash ?? '',
          },
          { replace: true },
        );
        setIsLoading(false);
        return;
      }

      const path = await getPostAuthPath(data.user!.id);
      setIsLoading(false);
      navigate(path, { replace: true });
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullName = `${firstName} ${lastName}`.trim();
    if (!email || !password || !firstName) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    if (password !== confirmPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match.",
        variant: "destructive"
      });
      return;
    }
    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      const { data, error } = await signUp(email, password, { full_name: fullName });

      if (error) throw error;

      if (data.user?.identities?.length === 0) {
        setShowResendConfirm(false);
        setShowSignUp(false);
        toast({
          title: "Account already exists",
          description: "This email is already registered. Sign in below, or use Forgot password if you don't remember it.",
        });
      } else if (data.session) {
        toast({
          title: "Account created",
          description: "You're signed in. Let's finish setting up your hotel workspace.",
        });

        if (data.user?.id) {
          const path = await getPostAuthPath(data.user.id);
          setIsLoading(false);
          navigate(path, { replace: true });
          return;
        }
      } else {
        setShowResendConfirm(true);
        setShowSignUp(false);
        toast({
          title: "Confirm your email",
          description: "We sent a Vesta CFO confirmation link. Open it to finish creating your account.",
        });
      }

      setPassword('');
      setConfirmPassword('');
      setFirstName('');
      setLastName('');
      setReferralCode('');
      setIsLoading(false);
    } catch (error: any) {
      toast({
        title: "Sign up failed",
        description: error.message || "Could not create your account.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      // Use our custom Resend-based password reset with production domain
      const { data, error } = await supabase.functions.invoke('send-password-reset', {
        body: {
          email: resetEmail,
          redirectUrl: getRedirectUrl('/auth', import.meta.env.DEV)
        }
      });
      
      if (error) {
        throw new Error(await getSupabaseFunctionErrorMessage(error, 'Failed to send password reset email.'));
      }
      if (data?.error) throw new Error(data.error);
      
      toast({ title: "Password reset sent", description: "Check your email for the reset link." });
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to send password reset email.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || !confirmNewPassword) {
      toast({
        title: "Missing information",
        description: "Please enter and confirm your new password.",
        variant: "destructive"
      });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast({
        title: "Password mismatch",
        description: "Passwords do not match.",
        variant: "destructive"
      });
      return;
    }
    if (newPassword.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters.",
        variant: "destructive"
      });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      
      // Clear the recovery mode flag using the utility function
      clearRecoveryMode();
      
      toast({ title: "Password updated!", description: "You can now sign in with your new password." });
      setShowSetNewPassword(false);
      setNewPassword('');
      setConfirmNewPassword('');
      // Sign out so they can sign in fresh with new password
      await supabase.auth.signOut();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update password.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      // Flag for new Google signups so onboarding org creation runs after OAuth redirect
      if (showSignUp) {
        localStorage.setItem('vesta_pending_google_signup', '1');
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // Same tab origin (required for Vercel previews); finish OAuth on /auth, then route below.
          redirectTo: getRedirectUrl('/auth', true),
        },
      });
      if (error) {
        localStorage.removeItem('vesta_pending_google_signup');
        toast({ title: "Google sign in failed", description: error.message, variant: "destructive" });
        setIsLoading(false);
      }
    } catch (error: any) {
      localStorage.removeItem('vesta_pending_google_signup');
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-vesta-cream">
        <Loader2 className="h-8 w-8 animate-spin text-vesta-gold" />
      </div>
    );
  }

  return (
    <>
      <AuthPageLayout>
        <Link to="/" className="auth-back">
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>
        <div className="auth-card">
              <AnimatePresence mode="wait">
                {showSetNewPassword ? (
                <motion.div
                  key="set-password"
                  initial={{ opacity: 0, x: 28, filter: 'blur(6px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -20, filter: 'blur(4px)' }}
                  transition={morphSpringSoft}
                  className="space-y-8"
                >
                  <h1 className="auth-title">Set New Password</h1>
                  <p className="auth-sub">Enter your new password below.</p>

                  <form onSubmit={handleSetNewPassword} className="space-y-4">
                    <UnderlineInput
                      type="password"
                      placeholder="New password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <UnderlineInput
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                    />

                    <button type="submit" className="b b-primary w-full b-lg mt-2" disabled={isLoading}>
                      {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</> : 'Update Password'}
                    </button>
                  </form>
                </motion.div>
              ) : showForgotPassword ? (
                <motion.div
                  key="forgot"
                  initial={{ opacity: 0, x: 28, filter: 'blur(6px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -20, filter: 'blur(4px)' }}
                  transition={morphSpringSoft}
                  className="space-y-8"
                >
                  <h1 className="auth-title">Reset Password</h1>

                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <UnderlineInput
                      type="email"
                      placeholder="Enter your email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />

                    <button type="submit" className="b b-primary w-full b-lg" disabled={isLoading}>
                      {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : 'Send Reset Link'}
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="w-full text-sm text-[var(--rd-text-2)] hover:text-[var(--vesta-navy)]"
                    >
                      Back to Sign In
                    </button>
                  </form>
                </motion.div>
              ) : showSignUp ? (
                <motion.div
                  key="signup"
                  initial={{ opacity: 0, x: 28, filter: 'blur(6px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -20, filter: 'blur(4px)' }}
                  transition={morphSpringSoft}
                  className="space-y-6"
                >
                  <div>
                    <h1 className="auth-title">
                      {teamInvite ? 'Accept Invitation' : 'Create Account'}
                    </h1>
                    {teamInvite ? (
                      <p className="auth-sub">
                        Already have an account?{' '}
                        <button type="button" onClick={() => setShowSignUp(false)} className="auth-link">
                          Sign in instead
                        </button>
                      </p>
                    ) : (
                      <p className="auth-sub">
                        Already have an account?{' '}
                        <button type="button" onClick={() => setShowSignUp(false)} className="auth-link">
                          Sign in
                        </button>
                      </p>
                    )}
                  </div>

                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <UnderlineInput
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                      <UnderlineInput
                        placeholder="Last name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>

                    <UnderlineInput type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    <UnderlineInput type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <UnderlineInput type="password" placeholder="Confirm password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                    <UnderlineInput placeholder="Referral code (optional)" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} icon={<Gift className="w-5 h-5" />} />

                    <button type="submit" className="b b-primary w-full b-lg mt-2" disabled={isLoading}>
                      {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</> : 'Create Account'}
                    </button>
                  </form>

                  <p className="text-xs leading-relaxed text-[var(--rd-text-2)]">
                    By creating an account, you agree to Vesta&apos;s{' '}
                    <Link to="/terms" className="auth-link">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="auth-link">Privacy Policy</Link>
                    . AI insights and connected data may be incomplete or inaccurate and are provided for informational purposes, not professional financial advice.
                  </p>

                  <div className="auth-divider">Or</div>

                  <button type="button" onClick={handleGoogleSignIn} disabled={isLoading} className="b b-outline w-full b-lg flex items-center justify-center gap-3">
                    <GoogleLogo className="h-5 w-5" />
                    Sign up with Google
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="signin"
                  initial={{ opacity: 0, x: 28, filter: 'blur(6px)' }}
                  animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, x: -20, filter: 'blur(4px)' }}
                  transition={morphSpringSoft}
                  className="space-y-6"
                >
                  <div>
                    <h1 className="auth-title">Welcome Back</h1>
                    <p className="auth-sub">
                      {"Don't have an account? "}
                      <button type="button" onClick={() => setShowSignUp(true)} className="auth-link">
                        Create one
                      </button>
                    </p>
                  </div>

                  <form onSubmit={handleSignIn} className="space-y-4">
                    <UnderlineInput type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                    <UnderlineInput type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />

                    <button type="submit" className="b b-primary w-full b-lg mt-2" disabled={isLoading}>
                      {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in...</> : 'Sign in'}
                    </button>
                  </form>

                  {showResendConfirm && (
                    <p className="text-sm text-center text-[var(--rd-text-2)]">
                      Didn&apos;t get the confirmation email?{' '}
                      <button
                        type="button"
                        onClick={handleResendConfirmation}
                        disabled={isLoading}
                        className="auth-link"
                      >
                        Resend confirmation email
                      </button>
                    </p>
                  )}

                  <div className="auth-divider">Or</div>

                  <button type="button" onClick={handleGoogleSignIn} disabled={isLoading} className="b b-outline w-full b-lg flex items-center justify-center gap-3">
                    <GoogleLogo className="h-5 w-5" />
                    Continue with Google
                  </button>

                  <div className="text-center">
                    <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-[var(--rd-text-2)] hover:text-[var(--vesta-navy)]">
                      Forgot password? <span className="auth-link">Reset it</span>
                    </button>
                  </div>
                </motion.div>
              )}
              </AnimatePresence>
        </div>
      </AuthPageLayout>

      <ReferralCodesModal codes={generatedCodes} open={showCodesModal} onClose={() => { setShowCodesModal(false); navigate('/dashboard'); }} />
    </>
  );
};

export default Auth;
